import React from 'react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, cn } from '@mf-tools/domains-ui';
import { ArrowRight, ShieldCheck, Users, Wallet } from 'lucide-react';
import { Container } from '../components';
import { scrollToId } from '../lib/scroll';

const OPEN_APP_URL = 'https://app.opspilott.com';

function HeroVisual(): React.ReactElement {
  return (
    <Card className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
      <CardHeader className="relative">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base">Founder Command Center</CardTitle>
            <p className="text-sm text-muted-foreground">Status, tasks, approvals, and audit trail — in one place.</p>
          </div>
          <div className="flex gap-2">
            <Badge variant="secondary">Compliance-ready</Badge>
            <Badge className="bg-primary/10 text-primary hover:bg-primary/15">Live</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Card className="border-muted/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4 text-primary" />
                People Ops
              </div>
              <div className="mt-3 space-y-2">
                <div className="h-2 w-full rounded bg-muted" />
                <div className="h-2 w-4/5 rounded bg-muted" />
                <div className="h-2 w-3/5 rounded bg-muted" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-muted/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Wallet className="h-4 w-4 text-primary" />
                Finance
              </div>
              <div className="mt-3 grid grid-cols-8 items-end gap-1">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-sm bg-primary/20"
                    style={{ height: `${14 + i * 6}px` }}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-muted/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Security & GRC
              </div>
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>SOC2 readiness</span>
                  <span className="text-foreground">74%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded bg-muted">
                  <div className="h-full w-3/4 bg-primary" aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-muted/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-medium">Today</div>
              <Badge variant="outline" className="text-muted-foreground">
                3 approvals pending
              </Badge>
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm">
                <span className="text-foreground">Approve vendor access policy</span>
                <Badge className="bg-primary/10 text-primary hover:bg-primary/15">Review</Badge>
              </div>
              <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm">
                <span className="text-foreground">Finalize onboarding checklist</span>
                <Badge variant="secondary">In progress</Badge>
              </div>
              <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm">
                <span className="text-foreground">Monthly cash visibility snapshot</span>
                <Badge variant="outline" className="text-muted-foreground">
                  Ready
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}

export interface HeroProps {
  className?: string;
}

export function Hero({ className }: HeroProps): React.ReactElement {
  return (
    <section className={cn('relative py-14 sm:py-20', className)}>
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
              Operations, on autopilot.
            </div>

            <h1 className="text-balance text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
              You build the product. OpsPilott runs the company.
            </h1>

            <p className="text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
              OpsPilott is the all-in-one operations co-pilot for early-stage startups — hiring, people ops, finance,
              security, and compliance in one platform, so founders can move fast without vendor chaos.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg">
                <a href={OPEN_APP_URL} target="_blank" rel="noreferrer">
                  Open App <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => scrollToId('contact')}
                aria-label="Talk to us (scroll to contact form)"
              >
                Talk to us
              </Button>
            </div>

            <ul className="grid gap-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                Replace 5–10 vendors with one platform
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                Move fast without operational drag
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                Compliance-ready without the overhead
              </li>
            </ul>
          </div>

          <div className="lg:pl-6">
            <HeroVisual />
          </div>
        </div>
      </Container>
    </section>
  );
}

